'use client';

import { useState } from 'react';
import { useTranslation } from '@/i18n/LanguageProvider';
import Link from '@/components/LocaleLink';
import {
  Wallet, Globe, Users,
  ChevronRight, Play, Headphones,
  TrendingUp, Zap, Gift, Star, ShieldCheck, Lightbulb, Mic2
} from 'lucide-react';
import AppDownloadButtons from '@/components/AppDownloadButtons';

const STANDARD_SHARE = 0.6;
const BONUS_SHARE = 1;

export default function GuiasPage() {
  const { t } = useTranslation();
  const [tourPrice, setTourPrice] = useState(4.99);
  const [toursPublished, setToursPublished] = useState(5);
  const [downloadsPerTour, setDownloadsPerTour] = useState(20);
  const [bonusActive, setBonusActive] = useState(false);
  const share = bonusActive ? BONUS_SHARE : STANDARD_SHARE;
  const monthlySales = toursPublished * downloadsPerTour;
  const monthlyEarnings = tourPrice * monthlySales * share;
  const annualEarnings = monthlyEarnings * 12;
  const fmt = (n: number) =>
    n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '€';

  const benefits = [
    { icon: Wallet, title: t('guias.benefit1Title'), desc: t('guias.benefit1Desc') },
    { icon: Zap, title: t('guias.benefit2Title'), desc: t('guias.benefit2Desc') },
    { icon: Globe, title: t('guias.benefit3Title'), desc: t('guias.benefit3Desc') },
    { icon: Mic2, title: t('guias.guideCloneVoiceTitle'), desc: t('guias.guideCloneVoiceDesc') },
  ];

  const steps = [
    { step: 1, title: t('guias.step1Title'), desc: t('guias.step1Desc'), icon: Users },
    { step: 2, title: t('guias.step2Title'), desc: t('guias.step2Desc'), icon: Headphones },
    { step: 3, title: t('guias.step3Title'), desc: t('guias.step3Desc'), icon: Play },
    { step: 4, title: t('guias.step4Title'), desc: t('guias.step4Desc'), icon: TrendingUp },
  ];

  const advantages = [
    { icon: '💰', title: t('guias.adv1Title'), subtitle: t('guias.adv1Sub'), desc: t('guias.adv1Desc') },
    { icon: '⚡', title: t('guias.adv2Title'), subtitle: t('guias.adv2Sub'), desc: t('guias.adv2Desc') },
    { icon: '🌍', title: t('guias.adv3Title'), subtitle: t('guias.adv3Sub'), desc: t('guias.adv3Desc') },
    { icon: '🗣️', title: t('guias.adv4Title'), subtitle: t('guias.adv4Sub'), desc: t('guias.adv4Desc') },
    { icon: '📱', title: t('guias.adv5Title'), subtitle: t('guias.adv5Sub'), desc: t('guias.adv5Desc') },
    { icon: '💬', title: t('guias.adv6Title'), subtitle: t('guias.adv6Sub'), desc: t('guias.adv6Desc') },
  ];

  const faqs = [
    { q: t('guias.faq1Q'), a: t('guias.faq1A') },
    { q: t('guias.faq2Q'), a: t('guias.faq2A') },
    { q: t('guias.faq3Q'), a: t('guias.faq3A') },
    { q: t('guias.faq4Q'), a: t('guias.faq4A') },
    { q: t('guias.faq5Q'), a: t('guias.faq5A') },
  ];

  return (
    <div>
      {/* Hero */}
      <section className="bg-gradient-to-br from-secondary-600 via-secondary-500 to-primary-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block px-4 py-1 bg-white/20 rounded-full text-sm mb-4">
                🎯 {t('guias.earnBadge')}
              </div>
              <h1 className="text-4xl md:text-5xl font-bold leading-tight">
                {t('guias.heroTitle1')}{' '}
                <span className="text-accent">{t('guias.heroHighlight')}</span>
              </h1>
              <p className="mt-6 text-lg text-white/90">{t('guias.heroSubtitle')}</p>
              
              <div className="mt-8">
                <p className="text-sm text-white/80 mb-3">{t('guias.downloadToApply')}</p>
                <AppDownloadButtons />
              </div>
              <div className="mt-4">
                <Link href="#como-funciona" className="text-white/80 hover:text-white inline-flex items-center text-sm">
                  <Play className="mr-1 w-4 h-4" />{t('guias.howItWorks')}
                </Link>
              </div>

              <div className="mt-12 grid grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold">60%</div>
                  <div className="text-white/70 text-sm">{t('guias.forYou')}</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold">0€</div>
                  <div className="text-white/70 text-sm">{t('guias.investment')}</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold">1h</div>
                  <div className="text-white/70 text-sm">{t('guias.toCreate')}</div>
                </div>
              </div>
            </div>

            <div>
              <div className="bg-white/10 backdrop-blur rounded-2xl p-6 sm:p-8 shadow-2xl border border-white/10">
                <div className="flex items-center justify-between mb-2 gap-3">
                  <h3 className="text-xl font-semibold">{t('guias.simulatorTitle')}</h3>
                  <span className="text-xs px-2 py-1 bg-white/20 text-white rounded-full font-semibold whitespace-nowrap">
                    {bonusActive ? '100%' : '60%'}
                  </span>
                </div>
                <p className="text-xs text-white/60 mb-6">{t('guias.adjustValues')}</p>

                <div className="space-y-6">
                  <div>
                    <div className="flex items-baseline justify-between mb-2">
                      <label htmlFor="tour-price" className="text-sm text-white/70">
                        {t('guias.tourPrice')}
                      </label>
                      <div className="text-2xl font-bold tabular-nums">{tourPrice.toFixed(2)}€</div>
                    </div>
                    <input
                      id="tour-price"
                      type="range"
                      min={0.99}
                      max={19.99}
                      step={0.01}
                      value={tourPrice}
                      onChange={(e) => setTourPrice(parseFloat(e.target.value))}
                      className="w-full accent-accent cursor-pointer"
                      aria-label={t('guias.tourPrice')}
                    />
                    <div className="flex justify-between text-[10px] text-white/50 mt-1">
                      <span>0.99€</span>
                      <span>19.99€</span>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-baseline justify-between mb-2">
                      <label htmlFor="tours-published" className="text-sm text-white/70">
                        {t('guias.toursPublished')}
                      </label>
                      <div className="text-2xl font-bold tabular-nums">{toursPublished}</div>
                    </div>
                    <input
                      id="tours-published"
                      type="range"
                      min={1}
                      max={30}
                      step={1}
                      value={toursPublished}
                      onChange={(e) => setToursPublished(parseInt(e.target.value, 10))}
                      className="w-full accent-accent cursor-pointer"
                      aria-label={t('guias.toursPublished')}
                    />
                    <div className="flex justify-between text-[10px] text-white/50 mt-1">
                      <span>1</span>
                      <span>30</span>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-baseline justify-between mb-2">
                      <label htmlFor="downloads-per-tour" className="text-sm text-white/70">
                        {t('guias.downloadsPerTour')}
                      </label>
                      <div className="text-2xl font-bold tabular-nums">{downloadsPerTour}</div>
                    </div>
                    <input
                      id="downloads-per-tour"
                      type="range"
                      min={1}
                      max={200}
                      step={1}
                      value={downloadsPerTour}
                      onChange={(e) => setDownloadsPerTour(parseInt(e.target.value, 10))}
                      className="w-full accent-accent cursor-pointer"
                      aria-label={t('guias.downloadsPerTour')}
                    />
                    <div className="flex justify-between text-[10px] text-white/50 mt-1">
                      <span>1</span>
                      <span>200</span>
                    </div>
                  </div>

                  <label
                    htmlFor="bonus-toggle"
                    className="flex items-center justify-between gap-3 p-3 rounded-xl bg-white/5 border border-white/10 cursor-pointer hover:bg-white/10 transition-colors"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <Gift className="w-4 h-4 text-accent flex-shrink-0" />
                      <span className="text-sm font-medium truncate">{t('guias.first3Months')}</span>
                    </div>
                    <div className="relative flex-shrink-0">
                      <input
                        id="bonus-toggle"
                        type="checkbox"
                        checked={bonusActive}
                        onChange={(e) => setBonusActive(e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-white/20 rounded-full peer-checked:bg-accent transition-colors" />
                      <div className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform peer-checked:translate-x-5" />
                    </div>
                  </label>

                  <hr className="border-white/20" />

                  <div>
                    <div className="flex items-center justify-between">
                      <label className="text-sm text-white/70">{t('guias.yourEarnings')}</label>
                      <span className="text-xs text-white/50 tabular-nums">
                        {monthlySales} {t('guias.monthlySales').toLowerCase()}
                      </span>
                    </div>
                    <div className="text-4xl font-bold text-accent tabular-nums">{fmt(monthlyEarnings)}</div>
                    <div className="mt-3 flex items-center justify-between text-sm">
                      <span className="text-white/70">{t('guias.annualEarnings')}</span>
                      <span className="font-semibold tabular-nums">{fmt(annualEarnings)}</span>
                    </div>
                    {bonusActive && (
                      <div className="mt-3 text-xs text-accent flex items-center gap-1">
                        <Gift className="w-3 h-3" /> {t('guias.welcomeBonus')}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="section-title">{t('guias.whyIteroGO')}</h2>
            <p className="section-subtitle">{t('guias.bestConditions')}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((b, i) => (
              <div key={i} className="text-center p-6">
                <div className="w-16 h-16 bg-primary-100 dark:bg-gray-700 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <b.icon className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2 dark:text-gray-100">{b.title}</h3>
                <p className="text-gray-600 dark:text-gray-300">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Free Tour */}
      <section className="py-20 bg-gradient-to-br from-accent/5 dark:from-gray-900 to-primary-50 dark:to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-1 bg-accent/10 text-accent rounded-full text-sm font-semibold mb-4">
              <Gift className="w-4 h-4" /> GRATIS
            </div>
            <h2 className="section-title">{t('guias.freeTourTitle')}</h2>
            <p className="section-subtitle max-w-2xl mx-auto">{t('guias.freeTourSubtitle')}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              { icon: Star, title: t('guias.freeTourBenefit1Title'), desc: t('guias.freeTourBenefit1Desc') },
              { icon: ShieldCheck, title: t('guias.freeTourBenefit2Title'), desc: t('guias.freeTourBenefit2Desc') },
              { icon: Gift, title: t('guias.freeTourBenefit3Title'), desc: t('guias.freeTourBenefit3Desc') },
            ].map((item, i) => (
              <div key={i} className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm hover:shadow-lg transition-shadow text-center">
                <div className="w-14 h-14 bg-accent/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-7 h-7 text-accent" />
                </div>
                <h3 className="text-lg font-semibold mb-2 dark:text-gray-100">{item.title}</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="como-funciona" className="py-20 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="section-title">{t('guias.howItWorks')}</h2>
            <p className="section-subtitle">{t('guias.stepsSubtitle')}</p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {steps.map((item) => (
              <div key={item.step} className="text-center">
                <div className="relative">
                  <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">{item.step}</div>
                  {item.step < 4 && <div className="hidden md:block absolute top-8 left-[60%] w-full h-0.5 bg-gray-200 dark:bg-gray-700" />}
                </div>
                <h3 className="text-lg font-semibold mb-2 dark:text-gray-100">{item.title}</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why choose IteroGO */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="section-title">{t('guias.whyChoose')}</h2>
            <p className="section-subtitle">{t('guias.bestConditionsCreate')}</p>
          </div>
          <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-8">
            {advantages.map((item, i) => (
              <div key={i} className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-6 hover:shadow-lg transition-shadow">
                <div className="text-4xl mb-4">{item.icon}</div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-1">{item.title}</h3>
                <p className="text-sm font-semibold text-primary mb-3">{item.subtitle}</p>
                <p className="text-gray-600 dark:text-gray-300 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
          {/* Pro Tip */}
          <div className="max-w-3xl mx-auto mt-12 bg-gradient-to-r from-amber-50 dark:from-gray-800 to-orange-50 dark:to-gray-800 border border-amber-200 dark:border-gray-700 rounded-2xl p-6 flex gap-4 items-start">
            <div className="flex-shrink-0 w-10 h-10 bg-amber-100 dark:bg-gray-700 rounded-xl flex items-center justify-center">
              <Lightbulb className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h4 className="font-bold text-gray-900 dark:text-gray-100 mb-1">{t('guias.proTipTitle')}</h4>
              <p className="text-gray-700 dark:text-gray-300 text-sm">{t('guias.proTipDesc')}</p>
            </div>
          </div>

          <div className="mt-12">
            <AppDownloadButtons className="justify-center" />
          </div>
          <p className="text-center mt-3 text-gray-500 dark:text-gray-400 text-sm">{t('guias.noCommitment')}</p>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="section-title">{t('faq.title')}</h2>
          </div>
          <div className="space-y-4">
            {faqs.map((item, i) => (
              <details key={i} className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm group">
                <summary className="font-semibold cursor-pointer list-none flex justify-between items-center dark:text-gray-100">
                  {item.q}<ChevronRight className="w-5 h-5 text-gray-400 dark:text-gray-500 group-open:rotate-90 transition-transform" />
                </summary>
                <p className="mt-4 text-gray-600 dark:text-gray-300">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-primary-600 to-secondary-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{t('guias.ctaTitle')}</h2>
          <p className="text-lg text-white/90 mb-8">{t('guias.ctaSubtitle')}</p>
          <div className="flex justify-center">
            <AppDownloadButtons className="justify-center" />
          </div>
          <p className="mt-4 text-white/70 text-sm">{t('guias.noCardNeeded')}</p>
        </div>
      </section>
    </div>
  );
}
